import { NavLink, Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, Briefcase, IndianRupee, Megaphone, User, MessageCircle, LogOut, Bell, Users, Tags,
  CreditCard, Wallet, Send, Palette, ShieldCheck, Settings, Menu, Clock, Ban, XCircle,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { Logo, Avatar } from "@/components/common";
import { NotificationBell } from "@/components/NotificationBell";
import { Footer, PolicyLinks } from "@/components/Footer";
import { cn } from "@/lib/utils";

function ProfileMenu({ items }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="rounded-full ring-offset-background focus:outline-none focus:ring-2 focus:ring-primary/50" data-testid="profile-menu-trigger">
          <Avatar name={user?.full_name} className="h-10 w-10" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-card border-border rounded-xl">
        <div className="px-3 py-2">
          <p className="text-sm font-medium truncate">{user?.full_name}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
        <DropdownMenuSeparator />
        {items.map(([label, to, Icon]) => (
          <DropdownMenuItem key={to} onClick={() => nav(to)} data-testid={`profile-menu-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <Icon className="h-4 w-4 mr-2" />{label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-button" className="text-red-400">
          <LogOut className="h-4 w-4 mr-2" />Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const CREATOR_NAV = [
  ["Home", "/dashboard", LayoutDashboard, "home"],
  ["Deals", "/deals", Briefcase, "deals"],
  ["Earnings", "/earnings", IndianRupee, "earnings"],
  ["Channel", "/channel", Megaphone, "channel"],
  ["Profile", "/profile", User, "profile"],
];

function StatusGate({ status }) {
  const { branding } = useBranding();
  const map = {
    PENDING: [Clock, "Your application is under review", `Thanks for joining ${branding.website_name}. Our team is reviewing your Instagram profile — you'll get a notification once approved.`],
    SUSPENDED: [Ban, "Account suspended", `Your account is currently suspended. Please contact ${branding.website_name} Support.`],
    REJECTED: [XCircle, "Application not approved", `Your application was not approved at this time. You can contact ${branding.website_name} Support for details.`],
  };
  const [Icon, title, desc] = map[status] || map.PENDING;
  return (
    <div className="max-w-xl mx-auto uc-card p-10 text-center fade-up" data-testid="status-gate">
      <div className="h-16 w-16 mx-auto rounded-2xl border border-primary/30 bg-primary/5 grid place-items-center mb-6">
        <Icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
      </div>
      <p className="eyebrow mb-2" data-testid="status-gate-status">Status · {status}</p>
      <h2 className="text-2xl font-semibold">{title}</h2>
      <p className="text-sm text-muted-foreground mt-3" data-testid="status-gate-message">{desc}</p>
      <div className="flex flex-wrap gap-3 justify-center mt-8">
        <Button asChild className="rounded-full" data-testid="status-gate-message-support"><Link to="/messages">Message Support</Link></Button>
        <Button asChild variant="outline" className="rounded-full" data-testid="status-gate-profile"><Link to="/profile">View Profile</Link></Button>
      </div>
    </div>
  );
}

const OPEN_PATHS = ["/profile", "/messages", "/notifications"];

export function CreatorLayout() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const approved = user?.status === "APPROVED";
  const gated = !approved && !OPEN_PATHS.some((p) => pathname.startsWith(p));
  return (
    <div className="min-h-screen grain pb-24 md:pb-0">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/dashboard"><Logo /></Link>
          <nav className="hidden md:flex items-center gap-1">
            {[...CREATOR_NAV.slice(0, 4), ["Messages", "/messages", MessageCircle, "messages"]].map(([l, to, Icon, k]) => (
              <NavLink key={to} to={to} data-testid={`nav-top-${k}`} className={({ isActive }) => cn(
                "px-3.5 py-2 rounded-full text-sm flex items-center gap-2 transition-colors",
                isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground")}>
                <Icon className="h-4 w-4" strokeWidth={1.7} />{l}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2.5">
            <Link to="/messages" className="md:hidden h-10 w-10 rounded-full border border-border bg-card/60 grid place-items-center" data-testid="mobile-messages-link">
              <MessageCircle className="h-4 w-4" strokeWidth={1.7} />
            </Link>
            <NotificationBell />
            <ProfileMenu items={[["Profile", "/profile", User], ["Messages", "/messages", MessageCircle], ["Notifications", "/notifications", Bell]]} />
          </div>
        </div>
      </header>
      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {gated ? <StatusGate status={user?.status} /> : <Outlet />}
        <Footer />
      </main>
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-background/95 backdrop-blur-lg border-t border-border px-2 pt-2 pb-[max(env(safe-area-inset-bottom),0.6rem)]" data-testid="creator-bottom-nav">
        <div className="grid grid-cols-5">
          {CREATOR_NAV.map(([l, to, Icon, k]) => (
            <NavLink key={to} to={to} data-testid={`nav-bottom-${k}`} className={({ isActive }) => cn(
              "flex flex-col items-center gap-1 py-1 text-[10px] font-semibold tracking-wider uppercase transition-colors",
              isActive ? "text-primary" : "text-muted-foreground")}>
              <Icon className="h-5 w-5" strokeWidth={1.6} />{l}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

const ADMIN_NAV = [
  ["Overview", "/admin", LayoutDashboard, "overview"],
  ["Creators", "/admin/creators", Users, "creators"],
  ["Categories", "/admin/categories", Tags, "categories"],
  ["Brand Deals", "/admin/deals", Briefcase, "deals"],
  ["Earnings", "/admin/earnings", Wallet, "earnings"],
  ["Payments", "/admin/payments", CreditCard, "payments"],
  ["Channel", "/admin/channel", Megaphone, "channel"],
  ["Notifications", "/admin/notifications", Bell, "notifications"],
  ["Broadcasts", "/admin/broadcasts", Send, "broadcasts"],
  ["Messages", "/admin/messages", MessageCircle, "messages"],
  ["Branding", "/admin/branding", Palette, "branding"],
  ["Privacy Policy", "/admin/privacy", ShieldCheck, "privacy"],
  ["Settings", "/admin/settings", Settings, "settings"],
];

function AdminNavList({ onNavigate }) {
  return (
    <nav className="space-y-0.5">
      {ADMIN_NAV.map(([l, to, Icon, k]) => (
        <NavLink key={to} to={to} end={to === "/admin"} onClick={onNavigate} data-testid={`admin-nav-${k}`} className={({ isActive }) => cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors",
          isActive ? "bg-primary/10 text-primary border border-primary/20" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60 border border-transparent")}>
          <Icon className="h-4 w-4" strokeWidth={1.7} />{l}
        </NavLink>
      ))}
    </nav>
  );
}

export function AdminLayout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen grain">
      <aside className="hidden lg:flex flex-col w-64 fixed inset-y-0 left-0 z-30 border-r border-border bg-background/90 backdrop-blur-xl p-4" data-testid="admin-sidebar">
        <Link to="/admin" className="px-2 py-3 mb-4 block"><Logo /></Link>
        <p className="eyebrow px-3 mb-2">Admin</p>
        <div className="flex-1 overflow-y-auto"><AdminNavList /></div>
        <PolicyLinks className="px-3 pt-4 text-[11px] text-muted-foreground" />
      </aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 backdrop-blur-xl bg-background/80 border-b border-border/70">
          <div className="px-4 sm:px-8 h-16 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <button className="lg:hidden h-10 w-10 rounded-full border border-border grid place-items-center" data-testid="admin-mobile-menu-button"><Menu className="h-4 w-4" /></button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 bg-background border-border p-4 overflow-y-auto">
                  <SheetTitle className="sr-only">Navigation</SheetTitle>
                  <div className="py-3 mb-4"><Logo /></div>
                  <AdminNavList onNavigate={() => setOpen(false)} />
                </SheetContent>
              </Sheet>
              <div className="lg:hidden"><Logo /></div>
              <span className="hidden lg:inline-flex rounded-full border border-primary/30 bg-primary/10 text-primary text-[10px] font-bold tracking-[0.2em] px-3 py-1" data-testid="admin-role-badge">ADMIN</span>
            </div>
            <div className="flex items-center gap-2.5">
              <NotificationBell allPath="/admin/notifications" />
              <ProfileMenu items={[["Settings", "/admin/settings", Settings], ["Branding", "/admin/branding", Palette]]} />
            </div>
          </div>
        </header>
        <main className="relative z-10 px-4 sm:px-8 py-8 sm:py-10 max-w-[1400px]">
          <Outlet />
          <Footer />
        </main>
      </div>
    </div>
  );
}

export function AuthShell({ children, wide }) {
  const { branding } = useBranding();
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] grain">
      <div className="hidden lg:block relative overflow-hidden">
        <img src="https://images.unsplash.com/flagged/photo-1555604858-34f65cfdc1db?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="" className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-background/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Logo variant="login_logo_url" size="lg" testid="auth-logo" />
          <div className="max-w-md fade-up">
            <p className="eyebrow mb-4 text-primary">Instagram creators only</p>
            <h2 className="text-5xl font-semibold leading-[1.05]">{branding.tagline}</h2>
            <p className="text-sm text-muted-foreground mt-5">Brand collaborations, payouts and support — managed for you, in one private place.</p>
          </div>
        </div>
      </div>
      <div className="relative z-10 flex flex-col px-5 sm:px-10 py-8">
        <div className="lg:hidden mb-10"><Logo variant="login_logo_url" testid="auth-logo-mobile" /></div>
        <div className={cn("w-full mx-auto my-auto fade-up", wide ? "max-w-lg" : "max-w-md")}>{children}</div>
        <div className="mt-10 text-xs text-muted-foreground flex flex-col sm:flex-row gap-3 justify-between">
          <span>© {new Date().getFullYear()} {branding.website_name}</span>
          <PolicyLinks />
        </div>
      </div>
    </div>
  );
}
